import { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQueryClient } from "@tanstack/react-query";
import ChatForm from "./ChatForm";
import Message from "./Message";
import useGetQueryMessages from "../../hooks/useGetQueryMessages";
import useGetSingleQueryStatus from "../../hooks/useGetSingleQueryStatus";

export default function ChatRoom({
  showChatModal,
  setShowChatModal,
  generateQueryMessageObject,
  setGenerateQueryMessageObject,
}) {
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState([]);
  const [incomingMessage, setIncomingMessage] = useState(null);
  const [isConversationClosed, setIsConversationClosed] = useState(false);

  const scrollRef = useRef();

  const queryId = generateQueryMessageObject.queryId;

  const { data: fetchedMessages, refetch: refetchMessages } = useGetQueryMessages({
    queryId,
    enabled: Boolean(queryId),
  });

  const { refetch: refetchQueryStatus } = useGetSingleQueryStatus({
    queryId,
  });

  useEffect(() => {
    if (fetchedMessages) {
      setMessages(fetchedMessages);
    }
  }, [fetchedMessages]);

  useEffect(() => {
    if (generateQueryMessageObject.queryStatus === "Complete") {
      setIsConversationClosed(true);
    }
  }, [generateQueryMessageObject.queryStatus]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    if (incomingMessage) {
      setMessages((prev) => [...prev, incomingMessage]);
    }
  }, [incomingMessage]);

  const handleSend = (message) => {
    const newMessage = {
      id: messages.length + 1,
      createdAt: new Date(),
      message,
      isSender: true,
    };
    setMessages((prevMessages) => [...prevMessages, newMessage]);

    if (message === "Conversation is closed.") {
      setIsConversationClosed(true);
    }
  };

  const handleReopen = () => {
    setIsConversationClosed(false);
  };

  const getQueryMessages = () => {
    refetchMessages();
  };

  const GetQueryData = async () => {
    const { data } = await refetchQueryStatus();
    if (data) {
      setGenerateQueryMessageObject((prev) => ({
        ...prev,
        queryStatus: data.status,
      }));
      queryClient.invalidateQueries({ queryKey: ["query_logs"] });
    }
  };

  return (
    <Dialog open={showChatModal} onClose={() => setShowChatModal(false)} fullScreen>
      <DialogTitle>
        Conversation
        <IconButton
          onClick={() => setShowChatModal(false)}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Card sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
          <CardContent sx={{ flex: 1, p: 0, overflow: "hidden" }}>
            <Box
              sx={{
                p: 2,
                height: "100%",
                overflowY: "auto",
                bgcolor: "background.paper",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <List sx={{ padding: 0 }}>
                {messages.map((message) => (
                  <ListItem
                    key={message.id}
                    ref={scrollRef}
                    sx={{
                      padding: 0,
                      justifyContent:
                        message?.sender?.email === user_data.email ? "flex-end" : "flex-start",
                    }}
                  >
                    <Message message={message} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </CardContent>
          <Box sx={{ p: 2 }}>
            {isConversationClosed ? (
              <>
                <Box sx={{ textAlign: "center", p: 2 }}>This Query is closed.</Box>
                <Button variant="contained" onClick={handleReopen}>
                  Start Again
                </Button>
              </>
            ) : (
              <ChatForm
                onSend={handleSend}
                isConversationClosed={isConversationClosed}
                queryId={generateQueryMessageObject.queryId}
                fromUserId={generateQueryMessageObject.fromUserId}
                getQueryMessages={getQueryMessages}
                GetQueryData={GetQueryData}
              />
            )}
          </Box>
        </Card>
      </DialogContent>
    </Dialog>
  );
}
